import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useReviewFlow } from "@/components/schema/ReviewFlow";

// Version list for any stored record: diff against the previous version, restore an older one.
export default function HistoryDialog({ open, onClose, kind, recordId, canRestore, onRestored }) {
  const [rows, setRows] = useState([]);
  const [diff, setDiff] = useState(null);
  const flow = useReviewFlow((d) => { onRestored && onRestored(d); load(); });

  const base = `/history/${kind}/${recordId}`;
  const load = async () => {
    try { const { data } = await api.get(base); setRows(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { if (open) { setDiff(null); load(); } }, [open, base]); // eslint-disable-line react-hooks/exhaustive-deps

  const showDiff = async (v) => {
    const prev = rows.find((r) => r.version === v - 1);
    if (!prev) { setDiff({ version: v, changes: null }); return; }
    try {
      const { data } = await api.get(`${base}/diff`, { params: { frm: v - 1, to: v } });
      setDiff({ version: v, changes: data.changes });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const restore = (v) => flow.start(`Restore version ${v}`, ({ dryRun }) =>
    api.post(`${base}/restore/${v}`, null, { params: { dry_run: dryRun } }));

  return (
    <Dialog open={open} onOpenChange={(x) => !x && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Version history</DialogTitle></DialogHeader>
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
          {rows.length === 0 && <div className="p-4 text-sm text-slate-500">No history yet.</div>}
          {rows.map((r, i) => (
            <div key={r.id} className="px-3 py-2 flex items-center gap-3 text-sm" data-testid={`history-row-${r.version}`}>
              <span className="font-mono font-semibold w-10">v{r.version}</span>
              <span className="text-slate-600 flex-1 min-w-0 truncate">
                {new Date(r.at).toISOString().replace("T", " ").slice(0, 16)} UTC · {r.by_email || r.by} · {r.action}
              </span>
              <Button variant="ghost" size="sm" onClick={() => showDiff(r.version)}>Changes</Button>
              {canRestore && i > 0 && <Button variant="outline" size="sm" onClick={() => restore(r.version)}>Restore</Button>}
            </div>
          ))}
        </div>
        {diff && (
          <div className="border border-slate-200 rounded-lg p-3">
            <div className="text-xs uppercase tracking-[0.12em] text-purple-700 mb-2">Changes in v{diff.version}</div>
            {diff.changes === null ? <div className="text-xs text-slate-500">First version.</div>
              : diff.changes.length === 0 ? <div className="text-xs text-slate-500">No field changes.</div>
              : diff.changes.slice(0, 100).map((c, i) => (
                <div key={i} className="text-xs font-mono grid grid-cols-12 gap-2">
                  <span className="col-span-5 truncate">{c.path}</span>
                  <span className="col-span-3 truncate text-red-700 line-through">{JSON.stringify(c.before)}</span>
                  <span className="col-span-4 truncate text-emerald-700">{JSON.stringify(c.after)}</span>
                </div>
              ))}
          </div>
        )}
        {flow.dialog}
      </DialogContent>
    </Dialog>
  );
}
